import CustomPagination from '@/platform/common/components/CustomPagination/CustomPagination';
import NoDataRow from '@/platform/common/components/NoDataRow/NoDataRow';
import { useFilters } from '@/platform/hooks/useFilters';
import { useConfirmationStore } from '@/platform/stores/useConfirmationStore';
import {
    Badge,
    Button,
    Container,
    FilterBar,
    FilterBarDropdown,
    Menu,
    MenuButton,
    MenuItem,
    MenuItems,
    MenuSeparator,
    Segment,
    SegmentContent,
    SegmentFooter,
    SegmentHeader,
    Table,
    TableBody,
    TableCell,
    TableHeader,
    TableHeaderCell,
    TableRow,
} from '@/platform/ui';
import {
    DevicePhoneMobileIcon,
    EllipsisVerticalIcon,
    EnvelopeIcon,
    KeyIcon,
    LinkIcon,
    LinkSlashIcon,
    PencilIcon,
    PhoneIcon,
    PlusIcon,
    StarIcon,
    TrashIcon,
} from '@heroicons/react/24/outline';
import { Link, router, usePage } from '@inertiajs/react';
import { useState } from 'react';
import AttachOfficeContactModal from './components/AttachOfficeContactModal';
import ContactModal from './components/ContactModal';

const IndexView = () => {
    const { auth, contacts, office_contacts, organisations } = usePage().props;
    const { filters, updateFilters } = useFilters(['office_contacts']);
    const confirm = useConfirmationStore(state => state.confirm);

    const [contactModal, setContactModal] = useState(null);
    const [attachModal, setAttachModal] = useState(false);

    const customerId = auth.customer.id;
    const can = permission => auth.permissions?.includes(permission);
    const canAny = permissions => permissions.some(can);

    const sendPasswordReset = contact =>
        router.post(
            `/customers/${customerId}/contacts/${contact.id}/send-password-reset`,
            {},
            { preserveScroll: true, preserveState: true }
        );

    const confirmDeleteContact = contact =>
        confirm({
            title: 'Contactpersoon verwijderen?',
            description: `Weet je zeker dat je ${contact.name} wilt verwijderen? Deze persoon zal ook niet meer kunnen inloggen.`,
            confirmLabel: 'Verwijderen',
            onConfirm: close => {
                router.delete(
                    `/customers/${customerId}/contacts/${contact.id}`,
                    { preserveScroll: true, preserveState: true }
                );
                close();
            },
        });

    const confirmDetachOfficeContact = officeContact =>
        confirm({
            title: 'Contactpersoon ontkoppelen?',
            description: `Weet je zeker dat je ${officeContact.name} wilt ontkoppelen van deze klant?`,
            confirmLabel: 'Ontkoppelen',
            onConfirm: close => {
                router.delete(
                    `/customers/${customerId}/office-contacts/${officeContact.id}`,
                    { preserveScroll: true, preserveState: true }
                );
                close();
            },
        });

    const canDeleteContact = contact =>
        (!contact.user || contact.user.id !== auth.user.id) &&
        canAny(['delete contacts', 'delete users']);

    const canImpersonate = contact =>
        contact.user &&
        canAny([
            'impersonate users',
            'impersonate internal users',
            'impersonate all users',
        ]);

    return (
        <Container>
            <Segment>
                <SegmentHeader
                    header={`Contactpersonen (${contacts?.length || 0})`}
                >
                    {can('create contacts') && (
                        <Button
                            variant='primary'
                            onClick={() => setContactModal({})}
                        >
                            <PlusIcon className='size-5' />
                            Toevoegen
                        </Button>
                    )}
                </SegmentHeader>
                <SegmentContent>
                    <Table striped>
                        <TableHeader>
                            <TableRow>
                                <TableHeaderCell>Rol</TableHeaderCell>
                                <TableHeaderCell>Bedrijf</TableHeaderCell>
                                <TableHeaderCell>Naam</TableHeaderCell>
                                <TableHeaderCell>Functie</TableHeaderCell>
                                <TableHeaderCell>Afdeling</TableHeaderCell>
                                <TableHeaderCell>
                                    Contactgegevens
                                </TableHeaderCell>
                                <TableHeaderCell />
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {contacts?.length === 0 && (
                                <NoDataRow numberOfColumns={7} />
                            )}

                            {contacts?.map(contact => (
                                <TableRow key={contact.id}>
                                    <TableCell singleLine>
                                        {contact.user?.roles?.map(role => (
                                            <Badge key={role.id}>
                                                {role.name}
                                            </Badge>
                                        ))}
                                    </TableCell>
                                    <TableCell singleLine>
                                        {contact.company || auth.customer.name}
                                    </TableCell>
                                    <TableCell singleLine>
                                        <span className='inline-flex items-center gap-1'>
                                            {contact.name}
                                            {contact.is_primary && (
                                                <StarIcon
                                                    className='size-4 text-amber-500'
                                                    title='Hoofdcontactpersoon'
                                                />
                                            )}
                                        </span>
                                    </TableCell>
                                    <TableCell>{contact.function}</TableCell>
                                    <TableCell>{contact.department}</TableCell>
                                    <TableCell singleLine>
                                        <ContactDetails contact={contact} />
                                    </TableCell>
                                    <TableCell
                                        className='w-px'
                                        singleLine
                                        textAlign='right'
                                    >
                                        <Menu>
                                            <MenuButton>
                                                <EllipsisVerticalIcon className='size-5' />
                                            </MenuButton>
                                            <MenuItems anchor='bottom end'>
                                                {can('update contacts') && (
                                                    <MenuItem
                                                        icon={PencilIcon}
                                                        onClick={() =>
                                                            setContactModal(
                                                                contact
                                                            )
                                                        }
                                                    >
                                                        Wijzigen
                                                    </MenuItem>
                                                )}
                                                {contact.user &&
                                                    can('update users') && (
                                                        <MenuItem
                                                            icon={EnvelopeIcon}
                                                            onClick={() =>
                                                                sendPasswordReset(
                                                                    contact
                                                                )
                                                            }
                                                        >
                                                            Wachtwoordherstel
                                                            versturen
                                                        </MenuItem>
                                                    )}
                                                {canImpersonate(contact) && (
                                                    <>
                                                        <MenuSeparator />
                                                        <MenuItem
                                                            icon={KeyIcon}
                                                            onClick={() =>
                                                                router.post(
                                                                    `/impersonate/login/${contact.user.id}`
                                                                )
                                                            }
                                                        >
                                                            Inloggen
                                                        </MenuItem>
                                                    </>
                                                )}
                                                {canDeleteContact(contact) && (
                                                    <>
                                                        <MenuSeparator />
                                                        <MenuItem
                                                            className='text-red-600'
                                                            icon={TrashIcon}
                                                            onClick={() =>
                                                                confirmDeleteContact(
                                                                    contact
                                                                )
                                                            }
                                                        >
                                                            Verwijderen
                                                        </MenuItem>
                                                    </>
                                                )}
                                            </MenuItems>
                                        </Menu>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </SegmentContent>
            </Segment>

            {can('view office contacts') && (
                <Segment className='mt-6'>
                    <SegmentHeader
                        header={`Contactpersonen Overheid (${office_contacts?.meta?.total || 0})`}
                    >
                        <Button
                            variant='primary'
                            onClick={() => setAttachModal(true)}
                        >
                            <LinkIcon className='size-5' />
                            Koppelen
                        </Button>
                    </SegmentHeader>
                    <SegmentContent>
                        <FilterBar>
                            <FilterBarDropdown
                                label={`Organisatie: ${filters?.organisation ?? 'Alle'}`}
                                clearable
                                options={(organisations || []).map(
                                    organisation => ({
                                        id: organisation,
                                        name: organisation,
                                    })
                                )}
                                value={filters?.organisation ?? ''}
                                onChange={value =>
                                    updateFilters('organisation', value)
                                }
                            />
                        </FilterBar>

                        <Table striped>
                            <TableHeader>
                                <TableRow>
                                    <TableHeaderCell>
                                        Organisatie
                                    </TableHeaderCell>
                                    <TableHeaderCell>Kantoor</TableHeaderCell>
                                    <TableHeaderCell>Geslacht</TableHeaderCell>
                                    <TableHeaderCell>Naam</TableHeaderCell>
                                    <TableHeaderCell>
                                        Contactgegevens
                                    </TableHeaderCell>
                                    <TableHeaderCell>
                                        Postgegevens
                                    </TableHeaderCell>
                                    <TableHeaderCell>Postbus</TableHeaderCell>
                                    <TableHeaderCell />
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {office_contacts?.meta?.total === 0 && (
                                    <NoDataRow numberOfColumns={8} />
                                )}

                                {office_contacts?.data?.map(officeContact => (
                                    <TableRow key={officeContact.id}>
                                        <TableCell singleLine>
                                            {officeContact.organisation}
                                        </TableCell>
                                        <TableCell>
                                            {officeContact.office}
                                        </TableCell>
                                        <TableCell>
                                            {officeContact.gender}
                                        </TableCell>
                                        <TableCell>
                                            {officeContact.name}
                                        </TableCell>
                                        <TableCell singleLine>
                                            <ContactDetails
                                                contact={officeContact}
                                            />
                                        </TableCell>
                                        <TableCell singleLine>
                                            {officeContact.zipcode}
                                            <br />
                                            {officeContact.city}
                                        </TableCell>
                                        <TableCell>
                                            {officeContact.postbus}
                                        </TableCell>
                                        <TableCell
                                            className='w-px'
                                            singleLine
                                            textAlign='right'
                                        >
                                            <Menu>
                                                <MenuButton>
                                                    <EllipsisVerticalIcon className='size-5' />
                                                </MenuButton>
                                                <MenuItems anchor='bottom end'>
                                                    {can(
                                                        'update office contacts'
                                                    ) && (
                                                        <MenuItem
                                                            as={Link}
                                                            href={`/office-contacts/${officeContact.id}/edit`}
                                                            icon={PencilIcon}
                                                        >
                                                            Wijzigen
                                                        </MenuItem>
                                                    )}
                                                    <MenuItem
                                                        className='text-red-600'
                                                        icon={LinkSlashIcon}
                                                        onClick={() =>
                                                            confirmDetachOfficeContact(
                                                                officeContact
                                                            )
                                                        }
                                                    >
                                                        Ontkoppelen
                                                    </MenuItem>
                                                </MenuItems>
                                            </Menu>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </SegmentContent>
                    <SegmentFooter>
                        <CustomPagination
                            meta={office_contacts?.meta}
                            only={['office_contacts']}
                        />
                    </SegmentFooter>
                </Segment>
            )}

            {contactModal && (
                <ContactModal
                    contact={contactModal}
                    onClose={() => setContactModal(null)}
                />
            )}

            {attachModal && (
                <AttachOfficeContactModal
                    onClose={() => setAttachModal(false)}
                />
            )}
        </Container>
    );
};

const ContactDetails = ({ contact }) => (
    <div className='flex flex-col gap-0.5'>
        {contact.email && (
            <span className='inline-flex items-center gap-1.5'>
                <EnvelopeIcon className='size-4 shrink-0 text-gray-400' />
                {contact.email}
            </span>
        )}
        {contact.telephone_number && (
            <span className='inline-flex items-center gap-1.5'>
                <PhoneIcon className='size-4 shrink-0 text-gray-400' />
                {contact.telephone_number}
            </span>
        )}
        {contact.mobile_number && (
            <span className='inline-flex items-center gap-1.5'>
                <DevicePhoneMobileIcon className='size-4 shrink-0 text-gray-400' />
                {contact.mobile_number}
            </span>
        )}
    </div>
);

export default IndexView;
